import { Container, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import CardWithHeader from "../../components/cards/CardWithHeader";
import { SelectFormInput } from "../../components/inputs";
import RadioInput from "../../components/inputs/RadioInput";
import SubmitButton from "../../components/SubmitButton";
import { languages, SupportedLanguage } from "../../constants";
import { useApp } from "../../contexts/AppContext";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import { ResourceProvider } from "../../contexts/ResourceContext";
import withAuthentication from "../../hocs/withAuthentication";
import { MeasuringSystem } from "../../types";

const Page: React.FC = () => {
  const { user, updateUserConfig } = useAuth();
  const { updateActions, updateBreadcrumbs } = useApp();
  const { showError, showSuccess } = useError();
  const [weightHeightUnit, setWeightHeightUnit] =
    useState<MeasuringSystem>("metric");
  const [hormoneLevelUnit, setHormoneLevelUnit] = useState<"pg/ml" | "ng/ml">(
    "pg/ml"
  );
  
  const { currentLanguage, setLanguage, getTranslations } = useApp();
  const [selectedLanguage, setSelectedLanguage] = useState<SupportedLanguage>(currentLanguage);
  
  const languageOptions = Object.entries(languages).map(
    ([id, info]) => ({ label: info.label, value: id })
  );

  const settingsTranslations = getTranslations().settings;

  useEffect(() => {
    if (user?.config) {
      if (user.config.weightHeightUnit) {
        setWeightHeightUnit(user.config.weightHeightUnit);
      }
      if (user.config.hormoneLevelUnit) {
        setHormoneLevelUnit(user.config.hormoneLevelUnit);
      }
    }
  }, [user]);

  useEffect(() => {
    updateBreadcrumbs([{ label: settingsTranslations.breadcrumb.label }]);
  }, [currentLanguage]);

  useEffect(() => {
    updateActions([
      <SubmitButton key="save" onClick={handleSave}>
        {settingsTranslations.buttons.save}
      </SubmitButton>,
    ]);

    return () => {
      updateActions([]);
    };
  }, [currentLanguage, selectedLanguage]);

  const handleSave = useCallback(() => {
    const config = {
      ...(user?.config ?? {}),
      weightHeightUnit,
      hormoneLevelUnit,
      language: selectedLanguage,
    };
    if (selectedLanguage) {
      setLanguage(selectedLanguage);
    }
    updateUserConfig(config, 
      () => {
        showSuccess(settingsTranslations.messages.saveSuccess);
      },
      (_error) => {
        showError(settingsTranslations.messages.saveError);
      }
    );
  }, [weightHeightUnit, hormoneLevelUnit, selectedLanguage]);

  return (
    <ResourceProvider>
      <Container>
        <Stack spacing={2} mt={1}>
          <CardWithHeader title={settingsTranslations.measurementUnits.title}>
            <Stack spacing={2}>
              <RadioInput
                label={
                  settingsTranslations.measurementUnits.fields.weightAndHeight
                    .title
                }
                value={{
                  label:
                    weightHeightUnit === "metric"
                      ? settingsTranslations.measurementUnits.fields
                          .weightAndHeight.metric
                      : settingsTranslations.measurementUnits.fields
                          .weightAndHeight.imperial,
                  value: weightHeightUnit,
                }}
                options={[
                  {
                    label:
                      settingsTranslations.measurementUnits.fields
                        .weightAndHeight.metric,
                    value: "metric",
                  },
                  {
                    label:
                      settingsTranslations.measurementUnits.fields
                        .weightAndHeight.imperial,
                    value: "imperial",
                  },
                ]}
                onChange={(v) => {
                  console.log(v);
                  setWeightHeightUnit(v?.value as MeasuringSystem);
                }}
              />
              <RadioInput
                label={settingsTranslations.hormoneLevels.title}
                value={{
                  label: hormoneLevelUnit === "pg/ml" ? "pg/mL" : "ng/mL",
                  value: hormoneLevelUnit,
                }}
                options={[
                  { label: "pg/mL", value: "pg/ml" },
                  { label: "ng/mL", value: "ng/ml" },
                ]}
                onChange={(v) =>
                  setHormoneLevelUnit(v?.value as "pg/ml" | "ng/ml")
                }
              />
            </Stack>
          </CardWithHeader>

          <CardWithHeader title={settingsTranslations.language.title}>
            <SelectFormInput
              options={languageOptions}
              label={""}
              onChange={(value: string) => setSelectedLanguage(value as SupportedLanguage)}
              value={selectedLanguage}
            ></SelectFormInput>
          </CardWithHeader>
        </Stack>
      </Container>
    </ResourceProvider>
  );
};

export default withAuthentication(Page);
